import React, { useState, useEffect } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { Link, useSearchParams } from "react-router-dom";
import PageHeader from "../../../../core/common/crmComponents/PageHeader";
import { useProductsData } from "../hooks/useProductsData";
import AddProductModal from "../actions/AddProductModal";
import LoaderComponent from "../../../../core/common/crmComponents/LoaderComponent";
import ActionsDropdown from "../../../../core/common/crmComponents/ActionsDropdown";
import {
  MODULES,
  MODULES_ACTIONS,
} from "../../../../core/data/constants/moduleConstants";
import { all_routes } from "../../../router/all_routes";
import {
  TextField,
  Box,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material";
import CompanyFilter from "../../../../core/common/crmComponents/CompanyFilter";

import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import HasPermission from "../../../../core/common/wrapper/HasPermission";
import { convertToUTCString } from "../../../../utils/helpers/helper";
import { customMuiDateFilter } from "../../../../utils/helpers/customMuiDateFilter";
import ClearAllFilters from "../../../../core/common/crmComponents/filters/ClearAllFilters";
import { setFilterModel } from "../../../../core/data/redux/filterSlice";
import ReloadButton from "../../../../core/common/crmComponents/ReloadButton";
import queryClient from "../../../../config/queryClient";
import { setReloading } from "../../../../core/data/redux/moduleDataSlice";

const Products = () => {
  const API_URL = process.env.REACT_APP_BACKEND_API_URL;
  const filterModel = useSelector((state) => state.filter.filterModel);
  const dispatch = useDispatch();
  const reloading = useSelector((state) => state.moduleData.reloading);

  const [searchParams, setSearchParams] = useSearchParams();
  const route = all_routes;
  const user = JSON.parse(localStorage.getItem("user") || []);

  const { companyNameArr } = useSelector((state) => state.auth);

  const [selectedCompany, setSelectedCompany] = useState(() => {
    const companyIdFromParams = searchParams.get("companyid");

    return companyIdFromParams
      ? companyIdFromParams
      : user?.ofCompany
      ? user?.ofCompany?._id
      : companyNameArr.length > 0
      ? companyNameArr[0].value
      : null;
  });

  useEffect(() => {
    if (reloading) {
      queryClient.invalidateQueries(["products"]);
      dispatch(setReloading(false));
    }
  }, [reloading, dispatch]);

  const [searchProductName, setSearchProductName] = useState("");
  const [searchCategory, setSearchCategory] = useState("");
  const [searchKey, setSearchKey] = useState("productName"); // Default search key

  const { data, isLoading, isError, error } = useProductsData({
    ofCompany: selectedCompany,
  });

  const productsData = data?.data?.data || [];
  let count = 0;

  // Flatten the productsData array
  const processedRows = productsData.flatMap((row) => {
    return row.products.map((product, index) => {
      count++;
      let obj = {
        ...row,
        id: `${row._id}-${index}`, // Generate a unique ID for each product
        productName: product.name, // Add the individual product name
        category: row.name, // Add category name
        ofCompany: row.ofCompany.name,
        ofCompanyId: row.ofCompany._id,
        createdAt: row.createdAt, // Add createdAt date for the product
        updatedAt: row.updatedAt, // Add updatedAt date for the product
      };

      return obj;
    });
  });

  // Filter products based on the search input
  const handleSearch = () => {
    return processedRows.filter((row) => {
      const searchValue =
        searchKey === "productName" ? row.productName : row.category;
      return searchValue
        .toLowerCase()
        .includes(
          searchKey === "productName"
            ? searchProductName.toLowerCase()
            : searchCategory.toLowerCase()
        );
    });
  };

  const filteredRows = handleSearch();

  const columns = [
    {
      field: "action",
      headerName: "Action",
      sortable: false,
      flex: 0.4,
      renderCell: (params) => (
        <>
          <ActionsDropdown
            params={params}
            path={route.productDetails}
            module={MODULES.PRODUCTS}
            showDetails={false}
          >
            <MenuItem className="dropdown-menu dropdown-menu-right py-0">
              <Link
                className="dropdown-item"
                onClick={() => {
                  navigator.clipboard
                    .writeText(
                      `  <iframe src='${API_URL}/api/v1/lead/leadform/form?ofCompany=${params?.row?.ofCompanyId}&productIntrested=${params?.row?.productName}' width="600" height="650"></iframe>`
                    )
                    .then(() => {
                      toast.success("Copied Link To Clipborad");
                    })
                    .catch((err) => {
                      toast.error("Failed To Copy Link");
                    });
                }}
              >
                <i className="ti ti-copy text-blue pe-2" /> Iframe Product Link
                Form
              </Link>
            </MenuItem>
               <MenuItem className="dropdown-menu dropdown-menu-right py-0">
              <Link
                className="dropdown-item"
                onClick={() => {
                  navigator.clipboard
                    .writeText(
                      `${API_URL}/api/v1/lead/leadform/form?ofCompany=${params?.row?.ofCompanyId}&productIntrested=${params?.row?.productName}`
                    )
                    .then(() => {
                      toast.success("Copied Link To Clipborad");
                    })
                    .catch((err) => {
                      toast.error("Failed To Copy Link");
                    });
                }}
              >
                <i className="ti ti-copy text-blue pe-2" /> Lead Form Link
              </Link>
            </MenuItem>
          </ActionsDropdown>
        </>
      ),
    },
    {
      field: "productName",
      headerName: "Product Name",
      flex: 1,
      renderCell: (params) => (
        <div className="text-capitalize">{params.value || "N/A"}</div>
      ),
    },
    {
      field: "category",
      headerName: "Product Category",
      flex: 1,
      renderCell: (params) => (
        <div className="text-capitalize">
          {params.value ? params.value.split("-").join(" ") : "N/A"}
        </div>
      ),
    },
    {
      field: "ofCompany",
      headerName: "Company",
      flex: 1,
      renderCell: (params) => (
        <div className="text-capitalize">{params?.value}</div>
      ),
    },
    {
      field: "createdAt",
      headerName: "Created At",
      flex: 1.4,
      filterable: true,
      valueFormatter: (params) =>
        params.value ? convertToUTCString(params.value) : "",
      filterOperators: customMuiDateFilter, // Use custom filtering logic
      renderCell: (params) => (
        <div>
          {params?.value
            ? convertToUTCString(params.value)
            : convertToUTCString(params?.row?.updatedAt)}
        </div>
      ),
    },
    {
      field: "updatedAt",
      headerName: "Updated At",
      flex: 1.4,
      valueFormatter: (params) =>
        params.value ? convertToUTCString(params.value) : "", // Display formatted value
      filterOperators: customMuiDateFilter,
      renderCell: (params) => (
        <div>
          {params?.value
            ? convertToUTCString(params.value)
            : convertToUTCString(params?.row?.createdAt)}
        </div>
      ),
    },
  ];

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content custom-content-padding">
          <div className="row">
            <div className="col-md-12">
              <PageHeader pageName="Products" totalCount={count} />
              <div className="card mb-0">
                <div className="card-header py-2">
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
                    {/* Filters */}
                    <div className="d-flex flex-wrap gap-3">
                      <ReloadButton moduleName={MODULES.PRODUCTS} />

                      <CompanyFilter
                        selectedCompany={selectedCompany}
                        setSelectedCompany={setSelectedCompany}
                        user={user}
                      />
                      <div className="d-flex gap-3 align-items-center">
                        <div className="col-sm-12">
                          <div className="input-group input-group-sm">
                            <select
                              className="form-select py-0"
                              value={searchKey}
                              onChange={(e) => setSearchKey(e.target.value)}
                            >
                              <option value="productName">Product Name</option>
                              <option value="category">Category</option>
                            </select>
                            <input
                              type="search"
                              placeholder="Search for ..."
                              className="form-control"
                              value={
                                searchKey === "productName"
                                  ? searchProductName
                                  : searchCategory
                              }
                              onChange={(e) =>
                                searchKey === "productName"
                                  ? setSearchProductName(e.target.value)
                                  : setSearchCategory(e.target.value)
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* /Filters */}

                    <div className="text-sm-end d-flex gap-2">
                      <ClearAllFilters />

                      <HasPermission
                        module={MODULES.PRODUCTS}
                        action={MODULES_ACTIONS.CREATE}
                      >
                        <Link
                          to="#"
                          className="btn btn-primary custom-btn-padding"
                          data-bs-toggle="modal"
                          data-bs-target="#add_product"
                        >
                          <i className="ti ti-square-rounded-plus me-2" />
                          Add New Product
                        </Link>
                      </HasPermission>
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  {/* Products List */}
                  <div style={{ width: "100%" }}>
                    {isLoading ? (
                      <LoaderComponent />
                    ) : isError ? (
                      <div>Error: {error.message}</div>
                    ) : (
                      <DataGrid
                        rows={filteredRows}
                        columns={columns.map((column) => ({
                          ...column,
                          headerClassName: "bg-light-200",
                        }))}
                        getRowId={(row) => row.id}
                        autoHeight
                        filterMode="client"
                        pagination
                        pageSizeOptions={[5]}
                        initialState={{
                          pagination: {
                            paginationModel: { pageSize: 10, page: 0 },
                          },
                          filter: {
                            filterModel: {
                              items: [], // Allows multiple column filters
                            },
                          },
                        }}
                        filterModel={filterModel}
                        onFilterModelChange={(newModel) =>
                          dispatch(setFilterModel(newModel))
                        }
                        slots={{
                          toolbar: GridToolbar, // Enable filter toolbar above the grid
                        }}
                        slotProps={{
                          toolbar: {
                            printOptions: { disableToolbarButton: true }, // Hide Print Button
                            csvOptions: { disableToolbarButton: true }, // Hide CSV Export
                          },
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /Page Wrapper */}

      <AddProductModal />
    </>
  );
};

export default Products;
